import type { GuidanceEventProperties } from './events';

/** The two events a guidance video emits. */
export type GuidanceVideoEvent =
  | 'guidance-video-played'
  | 'guidance-video-completed';

/** Where a tracker sends its events: `trackGuidanceEvent` in the product. */
export type GuidanceVideoSend = (
  event: GuidanceVideoEvent,
  properties: GuidanceEventProperties<GuidanceVideoEvent>
) => void;

/** The media handlers one opening of a guidance video wires to its `<video>`. */
export interface GuidanceVideoPlaybackTracker {
  onPlay: () => void;
  onEnded: () => void;
}

/**
 * Which guidance events one opening of a stage banner's video sends
 * (VALE-2067, GUIDE-15).
 *
 * **Each event is sent at most once per opening.** A `<video>` fires `play`
 * again on every resume after a pause and on every replay after the end, so
 * forwarding the raw media events would read a viewer who paused twice as
 * three plays: an over-count that looks like engagement, the same failure the
 * banner's own once-per-visit impression guards against. The unit is the
 * opening, because the player mounts when the dialog opens and unmounts when
 * it closes, so a second opening is a second tracker and a second play.
 *
 * `video` is the video's folder id (`GuidanceVideo.id`), never its title.
 */
export function createVideoPlaybackTracker(
  video: string,
  send: GuidanceVideoSend
): GuidanceVideoPlaybackTracker {
  let played = false;
  let completed = false;
  return {
    onPlay: () => {
      if (played) return;
      played = true;
      send('guidance-video-played', { video });
    },
    onEnded: () => {
      if (completed) return;
      completed = true;
      send('guidance-video-completed', { video });
    }
  };
}

/** As much of a media element as exclusive playback needs. */
export interface PausableMedia {
  pause: () => void;
}

let playing: PausableMedia | undefined;

/**
 * Make `media` the only guidance video playing: any other that is still
 * playing is paused first. Called from every player's `play` event, so two
 * copies of the player (a quick close and reopen, a remount) can never be
 * heard over each other.
 */
export function claimPlayback(media: PausableMedia): void {
  if (playing && playing !== media) playing.pause();
  playing = media;
}

/**
 * Stop whatever guidance video is playing. Called when the video dialog
 * closes. It pauses the element that actually emitted `play`, which is not
 * always the one a player's ref captured at mount: the owner heard the sound
 * carry on after closing because the pause went to the wrong element.
 */
export function stopPlayback(): void {
  playing?.pause();
  playing = undefined;
}
